import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createSiteContent, getSiteContent, updateSiteContentId } from '../../services/api';

const saveButtonStyle = {
  bgcolor: '#7BAAA7',
  '&:hover': { bgcolor: '#6A9391' },
  color: '#000',
  textTransform: 'none',
  fontWeight: 600,
};

export default function EditContactPage() {
  const navigate = useNavigate();

  const [contentId, setContentId] = useState(null);
  const [history, setHistory] = useState('');
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [location, setLocation] = useState({ address: '', locationName: '', mapsLink: '', institutionalEmail: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    document.title = 'CEDOMCA | Editar Página de Contato';
    (async () => {
      try {
        const { data } = await getSiteContent();
        if (data.length > 0) {
          const item = data[0];
          setContentId(item.id);
          setHistory(item.history || '');
          setProjects(item.projects || []);
          setContacts(item.contacts || []);
          setLocation({
            address: item.address || '',
            locationName: item.locationName || '',
            mapsLink: item.mapsLink || '',
            institutionalEmail: item.institutionalEmail || '',
          });
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddMember = () =>
    setContacts((prev) => [...prev, { name: '', role: '', email: '', phone: '' }]);

  const handleDeleteMember = (index) =>
    setContacts((prev) => prev.filter((_, i) => i !== index));

  const handleChange = (index, field, value) =>
    setContacts((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));

  const handleLocationChange = (field, value) =>
    setLocation((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        history,
        projects: projects.map(({ title, description, link }) => ({
          title,
          description,
          ...(link ? { link } : {}),
        })),
        contacts: contacts
          .filter((c) => c.name.trim())
          .map(({ name, email, phone, role }) => ({
            name,
            ...(email ? { email } : {}),
            ...(phone ? { phone } : {}),
            ...(role ? { role } : {}),
          })),
        ...(location.address ? { address: location.address } : {}),
        ...(location.locationName ? { locationName: location.locationName } : {}),
        ...(location.mapsLink ? { mapsLink: location.mapsLink } : {}),
        ...(location.institutionalEmail ? { institutionalEmail: location.institutionalEmail } : {}),
      };
      if (contentId) {
        await updateSiteContentId(contentId, payload);
      } else {
        const { data } = await createSiteContent(payload);
        setContentId(data.id);
      }
      setSnackbar({ open: true, message: 'Contatos salvos com sucesso!', severity: 'success' });
    } catch (err) {
      const raw = err?.response?.data?.message;
      const detail = Array.isArray(raw) ? raw[0] : (raw ?? 'Erro ao salvar contatos.');
      setSnackbar({ open: true, message: detail, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ minHeight: '100vh', bgcolor: '#f0f0f0' }} />;

  return (
    <Box sx={{ bgcolor: '#f0f0f0', minHeight: '100vh', p: '1.5rem' }}>

      {/* ─── Link de voltar ─── */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/site-content')}
        sx={{ textTransform: 'none', color: 'text.secondary', mb: 2, pl: 0, '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}
      >
        Voltar para seleção de seções
      </Button>

      {/* ─── Cabeçalho ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Editar Página de Contato</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Atualize informações de contato, endereço e equipe
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={saveButtonStyle}
        >
          Salvar Alterações
        </Button>
      </Stack>

      {/* ─── Seção Equipe ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsOutlinedIcon sx={{ color: '#e65100' }} />
            <Typography variant="subtitle1" fontWeight={700}>Equipe</Typography>
          </Stack>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={handleAddMember}
            sx={saveButtonStyle}
          >
            Adicionar Membro
          </Button>
        </Stack>

        {contacts.length === 0 ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic" align="center" sx={{ py: 3 }}>
            Nenhum membro cadastrado. Clique em &quot;Adicionar Membro&quot; para começar.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {contacts.map((contact, index) => (
              <Paper key={index} sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    Membro {index + 1}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleDeleteMember(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Nome Completo</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Dr. Maria Silva Santos"
                      value={contact.name}
                      onChange={(e) => handleChange(index, 'name', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Cargo/Função</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Diretora do CEDOMCA"
                      value={contact.role}
                      onChange={(e) => handleChange(index, 'role', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Email</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: email@ufam.edu.br"
                      value={contact.email}
                      onChange={(e) => handleChange(index, 'email', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ─── Seção Localização ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5, mt: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <PlaceOutlinedIcon sx={{ color: '#e65100' }} />
          <Typography variant="subtitle1" fontWeight={700}>Localização</Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Endereço</Typography>
            <TextField
              fullWidth size="small" placeholder="Ex: Av. General Rodrigo Octávio, 6200 - Coroado I"
              value={location.address}
              onChange={(e) => handleLocationChange('address', e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Nome do local</Typography>
            <TextField
              fullWidth size="small" placeholder="Ex: UFAM - Setor Norte"
              value={location.locationName}
              onChange={(e) => handleLocationChange('locationName', e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Link do Google Maps</Typography>
            <TextField
              fullWidth size="small" placeholder="https://maps.google.com/..."
              value={location.mapsLink}
              onChange={(e) => handleLocationChange('mapsLink', e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>E-mail Institucional</Typography>
            <TextField
              fullWidth size="small" placeholder="Ex: cedomca@ufam.edu.br"
              value={location.institutionalEmail}
              onChange={(e) => handleLocationChange('institutionalEmail', e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ─── Pré-visualização ─── */}
      <Paper sx={{ border: '1px solid #90caf9', boxShadow: 'none', borderRadius: 2, p: 2.5, mt: 3, bgcolor: '#e3f2fd' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <VisibilityOutlinedIcon sx={{ color: '#1565c0' }} />
          <Typography variant="subtitle1" fontWeight={700} color="#1565c0">Pré-visualização</Typography>
          <Typography variant="caption" color="#1976d2">(atualiza em tempo real)</Typography>
        </Stack>

        {/* Equipe */}
        {contacts.filter((c) => c.name.trim()).length > 0 && (
          <>
            <Typography variant="caption" fontWeight={700} color="#1565c0" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Equipe
            </Typography>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {contacts.filter((c) => c.name.trim()).slice(0, 4).map((contact, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper sx={{ p: 2, border: '1px solid #bbdefb', boxShadow: 'none', borderRadius: 2, bgcolor: '#fff', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: '#7BAAA7', color: '#fff', width: 36, height: 36, fontSize: 15, flexShrink: 0 }}>
                      {contact.name.trim().charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>{contact.name}</Typography>
                      {contact.role && (
                        <Typography variant="caption" color="#2e7d32" fontWeight={600} noWrap display="block">{contact.role}</Typography>
                      )}
                      {contact.email && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">{contact.email}</Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {contacts.filter((c) => c.name.trim()).length > 4 && (
              <Typography variant="caption" color="#1565c0" sx={{ mt: 1.5, display: 'block' }}>
                + {contacts.filter((c) => c.name.trim()).length - 4} membros adicionais
              </Typography>
            )}
          </>
        )}

        {/* Localização */}
        {(location.address || location.locationName || location.mapsLink || location.institutionalEmail) && (
          <>
            {contacts.filter((c) => c.name.trim()).length > 0 && (
              <Divider sx={{ my: 2, borderColor: '#90caf9' }} />
            )}
            <Typography variant="caption" fontWeight={700} color="#1565c0" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Localização
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {location.address && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Endereço:</strong> {location.address}
                </Typography>
              )}
              {(location.locationName || location.mapsLink) && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Localizado em:</strong>{location.locationName ? ` ${location.locationName}` : ''}
                  {location.mapsLink && (
                    <> —{' '}
                      <Box
                        component="a"
                        href={location.mapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#1565c0', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        Ver no Google Maps
                      </Box>
                    </>
                  )}
                </Typography>
              )}
              {location.institutionalEmail && (
                <Typography variant="body2" color="text.secondary">
                  <strong>E-mail Institucional:</strong> {location.institutionalEmail}
                </Typography>
              )}
            </Stack>
          </>
        )}

        {/* Estado vazio */}
        {contacts.filter((c) => c.name.trim()).length === 0
          && !location.address && !location.locationName && !location.mapsLink && !location.institutionalEmail && (
          <Typography variant="body2" color="#1976d2" fontStyle="italic" align="center" sx={{ py: 2 }}>
            Preencha os campos acima para ver a pré-visualização.
          </Typography>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
